// The start-then-poll loop behind an asynchronous vehicle lookup, with its dependencies injected
// so it is unit-testable without react-query or timers. The API waits for the provider itself and
// usually hands back a finished request; this loop only runs when that wait timed out.
import { IVehicle, IVehicleLookupRequest, VehicleLookupMode, VehicleLookupRequestStatus } from "../models/Vehicle";

const NUMERIC_STATUSES: VehicleLookupRequestStatus[] = ["Pending", "Processing", "Completed", "Failed", "Expired"];

/** The API serialises the status as a string; a numeric enum value is mapped the same way. */
export const normaliseLookupStatus = (status: unknown): VehicleLookupRequestStatus | null => {
  if (typeof status === "number") return NUMERIC_STATUSES[status] ?? null;
  if (typeof status === "string" && (NUMERIC_STATUSES as string[]).includes(status)) return status as VehicleLookupRequestStatus;

  return null;
};

export const isLookupInFlight = (status: unknown): boolean => {
  const normalised = normaliseLookupStatus(status);

  return normalised === "Pending" || normalised === "Processing";
};

export const isLookupTerminal = (status: unknown): boolean => !isLookupInFlight(status);

export class VehicleLookupTimeoutError extends Error {
  constructor(public readonly request: IVehicleLookupRequest | null) {
    super("The vehicle lookup timed out. Try again in a moment.");
    this.name = "VehicleLookupTimeoutError";
  }
}

export class VehicleLookupFailedError extends Error {
  constructor(public readonly request: IVehicleLookupRequest) {
    super(request.error || "Vehicle lookup failed");
    this.name = "VehicleLookupFailedError";
  }
}

export interface PollVehicleLookupDeps {
  start: () => Promise<IVehicleLookupRequest>;
  fetchRequest: (id: string) => Promise<IVehicleLookupRequest>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
  signal?: AbortSignal;
}

export interface PollVehicleLookupOptions {
  pollMs: number;
  maxMs: number;
}

/**
 * Starts a lookup and polls it until it is terminal. Throws VehicleLookupTimeoutError when the
 * deadline passes, the API reports the request stale, or the caller aborts.
 */
export async function pollVehicleLookup(
  deps: PollVehicleLookupDeps,
  options: PollVehicleLookupOptions
): Promise<IVehicleLookupRequest> {
  const started = deps.now();
  let request = await deps.start();

  while (isLookupInFlight(request.status)) {
    if (!request.id || request.isStale) throw new VehicleLookupTimeoutError(request);
    if (deps.signal?.aborted) throw new VehicleLookupTimeoutError(request);
    if (deps.now() - started >= options.maxMs) throw new VehicleLookupTimeoutError(request);

    await deps.sleep(options.pollMs);
    request = await deps.fetchRequest(request.id);
  }

  return request;
}

export interface VehicleLookupOutcome {
  vehicle: IVehicle | null;
  existed: boolean;
}

/**
 * Reads a terminal request the way each mode expects: a completed one is the vehicle; a miss is
 * null for the modes that only ask, and an error for the one that meant to create.
 */
export function lookupOutcome(request: IVehicleLookupRequest, mode: VehicleLookupMode): VehicleLookupOutcome {
  const status = normaliseLookupStatus(request.status);

  if (status === "Completed" && request.vehicle) {
    return { vehicle: request.vehicle, existed: !!request.existed };
  }

  if (status === "Expired") throw new VehicleLookupTimeoutError(request);

  if (mode !== "CreateIfMissing") return { vehicle: null, existed: false };

  throw new VehicleLookupFailedError(request);
}
